import React, { Fragment, useLayoutEffect, useMemo, useRef } from 'react';
import type { MappedChange } from '../../services/changesetsService';
import {
  buildChangePreviewModel,
  diffAppendixTables,
  resolveAppendixRowCells,
  type AppendixSnapshot,
  type AppendixTableDiff,
  type ParsedChangeValue,
} from '../../utils/changesetPreview';
import { buildRowGroupSections, buildRowMapFromGroupIds } from '../../utils/codeIndexUtils';
import type { CodeItemGroup, RuleNode } from '../../types/codeIndex';
import { AvailabilityBadge } from './AvailabilityBadge';

interface ChangesetChangePreviewProps {
  change: Pick<MappedChange, 'operation' | 'nodeType' | 'nodeId' | 'path' | 'oldValue' | 'newValue'>;
}

type FormatValuePreviewField = 'optionCode' | 'description' | 'rule' | 'status';
type FormatValuePreviewRow = Extract<ParsedChangeValue, { kind: 'format-value' }>['row'];
type FormatValuePreviewColumn = 'codeItem' | 'position' | FormatValuePreviewField;

const headerCellClassName = 'border border-[#d0d7de] bg-[#f6f8fa] px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-600';
const bodyCellClassName = 'border border-[#d0d7de] px-3 py-2 align-top text-sm text-gray-700';
const formatValueRowPreviewFields = new Set(['optionCode', 'description', 'rule', 'status']);

function formatPositionLabel(start: number, end: number): string {
  return start === end ? String(start) : `${start}-${end}`;
}

function renderRegions(regions: string[]) {
  if (regions.length === 0) {
    return <span className="text-sm text-gray-400">No regions</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {regions.map((region) => (
        <span
          key={region}
          className="inline-flex items-center rounded-full bg-[#F3F4F6] px-2 py-0.5 text-xs font-medium text-gray-700"
        >
          {region}
        </span>
      ))}
    </div>
  );
}

// Appendix references are domain ids; the letter is only known on the codesheet page, so show the count.
function renderAppendixRefs(refs: string[]) {
  if (refs.length === 0) {
    return <span className="text-sm text-gray-400">No appendix references</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {refs.map((ref) => (
        <span
          key={ref}
          title={ref}
          className="inline-flex items-center rounded-full bg-[#D8EFFF] px-2 py-0.5 text-xs font-medium text-[#0D47A1]"
        >
          Appendix
        </span>
      ))}
      <span className="text-xs text-gray-500">{refs.length === 1 ? '1 reference' : `${refs.length} references`}</span>
    </div>
  );
}

function renderScalarValue(value: string | number | boolean | null) {
  if (value === null || value === '') {
    return <span className="text-sm text-gray-400">Empty</span>;
  }

  if (typeof value === 'boolean') {
    return <span className="text-sm text-gray-700">{value ? 'Yes' : 'No'}</span>;
  }

  return <span className="text-sm text-gray-700 whitespace-pre-wrap break-words">{String(value)}</span>;
}

function renderStatusValue(status: string | null | undefined) {
  const normalizedStatus = typeof status === 'string' ? status.trim() : '';

  if (!normalizedStatus || normalizedStatus.toLowerCase() === 'empty') {
    return <span className="text-sm text-gray-400">Empty</span>;
  }

  return <span className="text-sm text-gray-700 whitespace-pre-wrap break-words">{normalizedStatus}</span>;
}

function isFormatValueRowPreviewField(path: string | null | undefined): path is FormatValuePreviewField {
  return formatValueRowPreviewFields.has(String(path || '').trim());
}

function extractFormatValueCodeFromIdentifier(value: string | null | undefined): string | null {
  const normalizedValue = String(value || '').trim();
  if (!normalizedValue) return null;

  const match = normalizedValue.match(/^([A-Za-z]?\d{2,})[-_]?/);
  return match?.[1] || null;
}

function getScalarPreviewLabel(value: ParsedChangeValue | null, treatAsStatus = false): string {
  if (!value) return 'Empty';

  if (value.kind === 'scalar') {
    if (treatAsStatus) {
      const normalizedStatus = typeof value.value === 'string' ? value.value.trim() : '';
      return !normalizedStatus || normalizedStatus.toLowerCase() === 'empty'
        ? 'Empty'
        : normalizedStatus;
    }

    if (value.value === null || value.value === '') {
      return 'Empty';
    }

    if (typeof value.value === 'boolean') {
      return value.value ? 'Yes' : 'No';
    }

    return String(value.value);
  }

  if (value.kind === 'raw') {
    return value.pretty || value.raw || 'Empty';
  }

  return 'Empty';
}

function PreviewFrame({
  title,
  tone,
  children,
}: {
  title: string;
  tone: 'neutral' | 'positive' | 'negative';
  children: React.ReactNode;
}) {
  const toneStyles = {
    neutral: 'border-gray-200 bg-white',
    positive: 'border-green-200 bg-[#F8FFF9]',
    negative: 'border-red-200 bg-[#FFF8F8]',
  };

  const titleStyles = {
    neutral: 'text-gray-500',
    positive: 'text-green-700',
    negative: 'text-red-700',
  };

  return (
    <div className={`rounded-lg border ${toneStyles[tone]} overflow-hidden`}>
      <div className={`border-b px-3 py-2 text-xs font-semibold uppercase tracking-[0.08em] ${titleStyles[tone]}`}>
        {title}
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function FieldBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">{label}</div>
      {children}
    </div>
  );
}

function RulePreview({ value }: { value: RuleNode | null }) {
  if (!value) {
    return <span className="text-sm text-gray-400">No availability rule</span>;
  }

  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
      <AvailabilityBadge value={value} disabled showConfigIcon={false} />
    </div>
  );
}

function DiffValueChip({
  tone,
  children,
}: {
  tone: 'before' | 'after';
  children: React.ReactNode;
}) {
  const className = tone === 'before'
    ? 'border border-red-200 bg-red-50 text-red-800'
    : 'border border-green-200 bg-green-50 text-green-800';

  return (
    <span className={`inline-flex items-center rounded-md px-2 py-1 text-sm font-medium ${className}`}>
      <span className="whitespace-nowrap">{children}</span>
    </span>
  );
}

function DiffRuleBlock({
  tone,
  value,
}: {
  tone: 'before' | 'after';
  value: Extract<ParsedChangeValue, { kind: 'rule' }> | null;
}) {
  const className = tone === 'before'
    ? 'border border-red-200 bg-red-50'
    : 'border border-green-200 bg-green-50';

  return (
    <div className={`inline-flex min-w-0 shrink-0 rounded-md p-2 ${className}`}>
      {value?.rule ? (
        <AvailabilityBadge value={value.rule} disabled showConfigIcon={false} />
      ) : (
        <span className="whitespace-nowrap text-sm text-gray-500">No availability rule</span>
      )}
    </div>
  );
}

function renderEmptyDiffTableCell() {
  return <div className="min-h-[24px]" />;
}

function inferCodeItemPositionLabel(code: string | null): string | null {
  const normalizedCode = String(code || '').trim();
  if (!normalizedCode) return null;

  const digitsMatch = normalizedCode.match(/(\d+)/);
  if (!digitsMatch?.[1]) return null;

  const parsedPosition = Number.parseInt(digitsMatch[1], 10);
  return Number.isFinite(parsedPosition) ? String(parsedPosition) : digitsMatch[1];
}

function getFormatValuePreviewColumnWidth(field: FormatValuePreviewField, column: FormatValuePreviewField): string {
  const widths = {
    optionCode: {
      optionCode: '160px',
      description: '72px',
      rule: '88px',
      status: '72px',
    },
    description: {
      optionCode: '72px',
      description: '240px',
      rule: '88px',
      status: '72px',
    },
    rule: {
      optionCode: '72px',
      description: '88px',
      rule: '320px',
      status: '72px',
    },
    status: {
      optionCode: '72px',
      description: '88px',
      rule: '88px',
      status: '168px',
    },
  } as const;

  return widths[field][column];
}

function getFormatValuePreviewTableMinWidth(field: FormatValuePreviewField): string {
  const widths = {
    optionCode: '460px',
    description: '500px',
    rule: '620px',
    status: '480px',
  } as const;

  return widths[field];
}

function getFormatValueSnapshotColumnWidth(column: FormatValuePreviewField): string {
  const widths = {
    optionCode: '120px',
    description: '200px',
    rule: '240px',
    status: '90px',
  } as const;

  return widths[column];
}

function getFormatValueIdentityColumnWidth(column: 'codeItem' | 'position'): string {
  const widths = {
    codeItem: '96px',
    position: '84px',
  } as const;

  return widths[column];
}

function renderFormatValueSnapshotOptionCell(row: FormatValuePreviewRow) {
  return (
    <div className="space-y-1">
      <div>{row.optionCode || <span className="text-gray-400">-</span>}</div>
      {row.group ? (
        <div className="text-xs font-medium text-gray-500">{row.group}</div>
      ) : null}
    </div>
  );
}

function renderFormatValueSnapshotDescriptionCell(row: FormatValuePreviewRow) {
  return row.description || <span className="text-gray-400">No description</span>;
}

function renderFormatValueSnapshotRuleCell(row: FormatValuePreviewRow) {
  return row.rule ? (
    <AvailabilityBadge value={row.rule} disabled showConfigIcon={false} />
  ) : (
    <span className="text-sm text-gray-400">No availability rule</span>
  );
}

function renderFormatValueSnapshotStatusCell(row: FormatValuePreviewRow) {
  return renderStatusValue(row.status);
}

function FormatValuePreviewTable({
  codeItemCell,
  positionCell,
  optionCell,
  descriptionCell,
  ruleCell,
  statusCell,
  optionWidth,
  descriptionWidth,
  ruleWidth,
  statusWidth,
  minWidth,
  autoScrollColumn,
}: {
  codeItemCell?: React.ReactNode;
  positionCell?: React.ReactNode;
  optionCell: React.ReactNode;
  descriptionCell: React.ReactNode;
  ruleCell: React.ReactNode;
  statusCell: React.ReactNode;
  optionWidth: string;
  descriptionWidth: string;
  ruleWidth: string;
  statusWidth: string;
  minWidth: string;
  autoScrollColumn?: FormatValuePreviewField | null;
}) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const changedCellRef = useRef<HTMLTableCellElement | null>(null);

  useLayoutEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    const changedCell = changedCellRef.current;

    if (!scrollContainer || !changedCell || !autoScrollColumn) return;

    const visibleLeft = scrollContainer.scrollLeft;
    const visibleRight = visibleLeft + scrollContainer.clientWidth;
    const cellLeft = changedCell.offsetLeft;
    const cellRight = cellLeft + changedCell.offsetWidth;
    const margin = 12;

    if (cellRight > visibleRight) {
      scrollContainer.scrollLeft = Math.max(0, cellRight - scrollContainer.clientWidth + margin);
      return;
    }

    if (cellLeft < visibleLeft) {
      scrollContainer.scrollLeft = Math.max(0, cellLeft - margin);
    }
  }, [autoScrollColumn, optionCell, descriptionCell, ruleCell, statusCell]);

  const renderCell = (column: FormatValuePreviewColumn, width: string, content: React.ReactNode) => (
    <td
      ref={autoScrollColumn === column ? changedCellRef : undefined}
      className={`${bodyCellClassName} bg-white`}
      style={{ width }}
    >
      {content}
    </td>
  );

  return (
    <div ref={scrollContainerRef} className="overflow-x-auto rounded-lg border border-[#d7e3ef] bg-white">
      <table className="w-full border-collapse" style={{ minWidth }}>
        <colgroup>
          {codeItemCell !== undefined ? <col style={{ width: getFormatValueIdentityColumnWidth('codeItem') }} /> : null}
          {positionCell !== undefined ? <col style={{ width: getFormatValueIdentityColumnWidth('position') }} /> : null}
          <col style={{ width: optionWidth }} />
          <col style={{ width: descriptionWidth }} />
          <col style={{ width: ruleWidth }} />
          <col style={{ width: statusWidth }} />
        </colgroup>
        <thead>
          <tr>
            {codeItemCell !== undefined ? <th className={headerCellClassName}>Code Item</th> : null}
            {positionCell !== undefined ? <th className={headerCellClassName}>Position</th> : null}
            <th className={headerCellClassName}>Options</th>
            <th className={headerCellClassName}>Description</th>
            <th className={headerCellClassName}>Availability Rules</th>
            <th className={headerCellClassName}>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr className="bg-[#F8FBFF]">
            {codeItemCell !== undefined ? renderCell('codeItem', getFormatValueIdentityColumnWidth('codeItem'), codeItemCell) : null}
            {positionCell !== undefined ? renderCell('position', getFormatValueIdentityColumnWidth('position'), positionCell) : null}
            {renderCell('optionCode', optionWidth, optionCell)}
            {renderCell('description', descriptionWidth, descriptionCell)}
            {renderCell('rule', ruleWidth, ruleCell)}
            {renderCell('status', statusWidth, statusCell)}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function FormatValueSnapshotTable({
  row,
  code,
}: {
  row: FormatValuePreviewRow;
  code: string | null;
}) {
  const positionLabel = inferCodeItemPositionLabel(code);

  return (
    <FormatValuePreviewTable
      codeItemCell={code ? <span className="font-semibold text-gray-900">{code}</span> : undefined}
      positionCell={positionLabel ? <span className="text-gray-700">{positionLabel}</span> : undefined}
      optionCell={renderFormatValueSnapshotOptionCell(row)}
      descriptionCell={renderFormatValueSnapshotDescriptionCell(row)}
      ruleCell={renderFormatValueSnapshotRuleCell(row)}
      statusCell={renderFormatValueSnapshotStatusCell(row)}
      optionWidth={getFormatValueSnapshotColumnWidth('optionCode')}
      descriptionWidth={getFormatValueSnapshotColumnWidth('description')}
      ruleWidth={getFormatValueSnapshotColumnWidth('rule')}
      statusWidth={getFormatValueSnapshotColumnWidth('status')}
      minWidth="560px"
    />
  );
}

function FormatValueUpdatedRowTable({
  field,
  oldPreview,
  newPreview,
  code,
}: {
  field: FormatValuePreviewField;
  oldPreview: ParsedChangeValue | null;
  newPreview: ParsedChangeValue | null;
  code: string | null;
}) {
  const positionLabel = inferCodeItemPositionLabel(code);

  const renderDiffCell = (column: FormatValuePreviewField) => {
    if (field !== column) {
      return renderEmptyDiffTableCell();
    }

    if (field === 'rule') {
      const oldRulePreview = oldPreview?.kind === 'rule' ? oldPreview : null;
      const newRulePreview = newPreview?.kind === 'rule' ? newPreview : null;

      return (
        <div className="overflow-x-auto">
          <div className="flex min-w-max items-center gap-2">
            <DiffRuleBlock tone="before" value={oldRulePreview} />
            <span className="shrink-0 text-sm font-semibold text-gray-400" aria-hidden="true">
              -&gt;
            </span>
            <DiffRuleBlock tone="after" value={newRulePreview} />
          </div>
        </div>
      );
    }

    const treatAsStatus = field === 'status';

    return (
      <div className="overflow-x-auto">
        <div className="flex min-w-max items-center gap-2 whitespace-nowrap">
          <DiffValueChip tone="before">
            {getScalarPreviewLabel(oldPreview, treatAsStatus)}
          </DiffValueChip>
          <span className="shrink-0 text-sm font-semibold text-gray-400" aria-hidden="true">
            -&gt;
          </span>
          <DiffValueChip tone="after">
            {getScalarPreviewLabel(newPreview, treatAsStatus)}
          </DiffValueChip>
        </div>
      </div>
    );
  };

  return (
    <FormatValuePreviewTable
      codeItemCell={code ? <span className="font-semibold text-gray-900">{code}</span> : undefined}
      positionCell={positionLabel ? <span className="text-gray-700">{positionLabel}</span> : undefined}
      optionCell={renderDiffCell('optionCode')}
      descriptionCell={renderDiffCell('description')}
      ruleCell={renderDiffCell('rule')}
      statusCell={renderDiffCell('status')}
      optionWidth={getFormatValuePreviewColumnWidth(field, 'optionCode')}
      descriptionWidth={getFormatValuePreviewColumnWidth(field, 'description')}
      ruleWidth={getFormatValuePreviewColumnWidth(field, 'rule')}
      statusWidth={getFormatValuePreviewColumnWidth(field, 'status')}
      minWidth={getFormatValuePreviewTableMinWidth(field)}
      autoScrollColumn={field}
    />
  );
}

function CodeItemSnapshotTable({ group }: { group: CodeItemGroup }) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const descriptionMap = useMemo(
    () => buildRowMapFromGroupIds<string>(group.rows, 'description'),
    [group.rows]
  );
  const availabilityMap = useMemo(
    () => buildRowMapFromGroupIds<RuleNode | null | undefined>(group.rows, 'availability'),
    [group.rows]
  );
  const sections = useMemo(
    () => buildRowGroupSections(group.rows),
    [group.rows]
  );

  const visibleSectionHeaderCount = sections.filter((section) => section.name).length;
  const renderedRowCount = Math.max(1, group.rows.length + visibleSectionHeaderCount + (group.title ? 1 : 0));
  const positionLabel = formatPositionLabel(group.codeItemPos.posRange[0], group.codeItemPos.posRange[1]);

  useLayoutEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0;
    }
  }, [group.codeItemPos.code, group.title, group.rows.length]);

  let identityCellsRendered = false;

  const renderIdentityCells = () => {
    if (identityCellsRendered) return null;
    identityCellsRendered = true;

    return (
      <>
        <td rowSpan={renderedRowCount} className={`${bodyCellClassName} bg-[#FAFBFC] min-w-[92px]`}>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-semibold text-gray-900">{group.codeItemPos.code}</span>
          </div>
        </td>
        <td rowSpan={renderedRowCount} className={`${bodyCellClassName} bg-[#FAFBFC] min-w-[70px]`}>
          {positionLabel}
        </td>
      </>
    );
  };

  const renderRow = (rowIndex: number) => {
    const row = group.rows[rowIndex];
    if (!row) return null;

    return (
      <tr key={`row-${row.rowId}-${rowIndex}`}>
        {renderIdentityCells()}
        <td className={`${bodyCellClassName} min-w-[92px]`}>{row.optionCode || <span className="text-gray-400">-</span>}</td>
        {descriptionMap[rowIndex]?.rowSpan > 0 ? (
          <td rowSpan={descriptionMap[rowIndex].rowSpan} className={`${bodyCellClassName} min-w-[132px] whitespace-pre-wrap break-words`}>
            {descriptionMap[rowIndex].value || <span className="text-gray-400">No description</span>}
          </td>
        ) : null}
        {availabilityMap[rowIndex]?.rowSpan > 0 ? (
          <td rowSpan={availabilityMap[rowIndex].rowSpan} className={`${bodyCellClassName} min-w-[168px]`}>
            {availabilityMap[rowIndex].value ? (
              <AvailabilityBadge value={availabilityMap[rowIndex].value} disabled showConfigIcon={false} />
            ) : (
              <span className="text-sm text-gray-400">No availability rule</span>
            )}
          </td>
        ) : null}
        <td className={`${bodyCellClassName} min-w-[92px]`}>{renderStatusValue(row.status)}</td>
      </tr>
    );
  };

  return (
    <div ref={scrollContainerRef} className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="min-w-[560px] w-full border-collapse">
        <thead>
          <tr>
            <th className={headerCellClassName}>Code Item</th>
            <th className={headerCellClassName}>Position</th>
            <th className={headerCellClassName}>Options</th>
            <th className={headerCellClassName}>Description</th>
            <th className={headerCellClassName}>Availability Rules</th>
            <th className={headerCellClassName}>Status</th>
          </tr>
        </thead>
        <tbody>
          {group.title ? (
            <tr>
              {renderIdentityCells()}
              <td colSpan={4} className={`${bodyCellClassName} bg-[#F8FAFC] font-semibold tracking-[0.08em] text-[#1D74D8] uppercase`}>
                {group.title}
              </td>
            </tr>
          ) : null}

          {group.rows.length === 0 ? (
            <tr>
              {renderIdentityCells()}
              <td colSpan={4} className={bodyCellClassName}>
                <span className="text-sm text-gray-400">No rows available</span>
              </td>
            </tr>
          ) : (
            sections.map((section, sectionIndex) => (
              <Fragment key={`section-${section.key}-${sectionIndex}`}>
                {section.name ? (
                  <tr>
                    {renderIdentityCells()}
                    <td colSpan={4} className={`${bodyCellClassName} bg-[#F8FAFC] font-semibold tracking-[0.08em] text-[#1D74D8] uppercase`}>
                      {section.name}
                    </td>
                  </tr>
                ) : null}
                {section.rowIndices.map((rowIndex) => renderRow(rowIndex))}
              </Fragment>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

type AppendixDiffSide = 'before' | 'after';

// Statuses that are shown on each side of a before/after pair.
const visibleRowStatuses: Record<AppendixDiffSide, ReadonlySet<string>> = {
  before: new Set(['removed', 'changed']),
  after: new Set(['added', 'changed']),
};

function appendixRowClassName(status: string | undefined): string {
  if (status === 'added') return 'bg-[#F0FFF4]';
  if (status === 'removed') return 'bg-[#FFF5F5]';
  return '';
}

// One table of an appendix snapshot. With `diffs` (one per table index) and a `side`, rows and
// cells that differ from the other side are highlighted.
function AppendixSnapshotTable({
  appendix,
  diffs,
  side,
}: {
  appendix: AppendixSnapshot;
  diffs?: AppendixTableDiff[];
  side?: AppendixDiffSide;
}) {
  if (appendix.tables.length === 0) {
    return <span className="text-sm text-gray-400">No table</span>;
  }

  return (
    <div className="space-y-3">
      {appendix.tables.map((table, tableIndex) => {
        const diff = side ? diffs?.[tableIndex] : undefined;
        const rowStatusByPosition = new Map(diff?.rows.map((row) => [row.position, row]) ?? []);
        const columnStatusByPosition = new Map(diff?.columns.map((column) => [column.position, column.status]) ?? []);
        const isVisibleStatus = (status: string | undefined) => Boolean(side && status && visibleRowStatuses[side].has(status));

        return (
          <div key={table.id || `table-${tableIndex}`} className="overflow-x-auto rounded-lg border border-gray-200 bg-white" data-testid="appendix-snapshot-table">
            {appendix.tables.length > 1 && table.name ? (
              <div className="border-b border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600">{table.name}</div>
            ) : null}
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  {table.columns.map((column) => {
                    const columnStatus = columnStatusByPosition.get(column.position);
                    const highlighted = isVisibleStatus(columnStatus);
                    return (
                      <th
                        key={column.id || `column-${column.position}`}
                        className={`${headerCellClassName} ${highlighted ? appendixRowClassName(columnStatus) || 'bg-[#FFF8C5]' : ''}`}
                        data-column-status={columnStatus || 'same'}
                      >
                        {column.title || column.name || <span className="text-gray-400">Column {column.position + 1}</span>}
                        {column.unit ? <span className="ml-1 font-normal normal-case tracking-normal text-gray-400">({column.unit})</span> : null}
                      </th>
                    );
                  })}
                  {table.columns.length === 0 ? <th className={headerCellClassName}>No columns</th> : null}
                </tr>
              </thead>
              <tbody>
                {table.rows.length === 0 ? (
                  <tr>
                    <td colSpan={Math.max(table.columns.length, 1)} className={bodyCellClassName}>
                      <span className="text-sm text-gray-400">No rows</span>
                    </td>
                  </tr>
                ) : table.rows.map((row) => {
                  const cells = resolveAppendixRowCells(table, row);
                  const rowDiff = rowStatusByPosition.get(row.position);
                  const rowStatus = isVisibleStatus(rowDiff?.status) ? rowDiff?.status : 'same';
                  const changedColumns = new Set(rowDiff?.changedColumnPositions ?? []);

                  return (
                    <tr key={row.id || `row-${row.position}`} className={appendixRowClassName(rowStatus)} data-row-status={rowStatus}>
                      {table.columns.map((column) => {
                        const cell = cells.get(column.position);
                        const changed = rowStatus === 'changed' && changedColumns.has(column.position);
                        return (
                          <td
                            key={column.id || `cell-${row.position}-${column.position}`}
                            className={`${bodyCellClassName} ${changed ? 'bg-[#FFF8C5] font-medium' : ''}`}
                            data-cell-status={changed ? 'changed' : 'same'}
                            title={cell?.notes || undefined}
                          >
                            {cell?.value ? <span className="whitespace-pre-wrap break-words">{cell.value}</span> : <span className="text-gray-400">-</span>}
                          </td>
                        );
                      })}
                      {table.columns.length === 0 ? <td className={bodyCellClassName}><span className="text-gray-400">-</span></td> : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

function renderPreviewValue(value: ParsedChangeValue | null) {
  if (!value) {
    return <span className="text-sm text-gray-400">No value</span>;
  }

  if (value.kind === 'code-item') {
    return <CodeItemSnapshotTable group={value.group} />;
  }

  if (value.kind === 'appendix') {
    return <AppendixSnapshotTable appendix={value.appendix} />;
  }

  if (value.kind === 'format-value') {
    return <FormatValueSnapshotTable row={value.row} code={value.code} />;
  }

  if (value.kind === 'rule') {
    return <RulePreview value={value.rule} />;
  }

  if (value.kind === 'regions') {
    return (
      <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
        {renderRegions(value.regions)}
      </div>
    );
  }

  if (value.kind === 'appendixRefs') {
    return (
      <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
        {renderAppendixRefs(value.refs)}
      </div>
    );
  }

  if (value.kind === 'scalar') {
    return (
      <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
        {renderScalarValue(value.value)}
      </div>
    );
  }

  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
      <pre className="text-xs text-gray-700 whitespace-pre-wrap break-words font-mono">
        {value.pretty || value.raw || 'No value'}
      </pre>
    </div>
  );
}

export default function ChangesetChangePreview({ change }: ChangesetChangePreviewProps) {
  const previewModel = useMemo(() => buildChangePreviewModel(change), [change]);
  const deletedFormatValuePreview = previewModel.operation === 'delete' && previewModel.oldPreview?.kind === 'format-value'
    ? previewModel.oldPreview
    : null;
  const formatValuePreview = previewModel.newPreview?.kind === 'format-value'
    ? previewModel.newPreview
    : (previewModel.oldPreview?.kind === 'format-value' ? previewModel.oldPreview : null);
  const formatValueCode = formatValuePreview?.code
    || extractFormatValueCodeFromIdentifier(previewModel.nodeId)
    || (previewModel.nodeType === 'FormatValue' ? extractFormatValueCodeFromIdentifier(previewModel.path) : null);
  const formatValueRowPreviewField = isFormatValueRowPreviewField(previewModel.path)
    ? previewModel.path
    : null;

  if (!previewModel.oldPreview && !previewModel.newPreview) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white px-3 py-3 text-sm text-gray-500">
        No detailed preview is available for this change.
      </div>
    );
  }

  if (previewModel.operation === 'update') {
    if (previewModel.oldPreview?.kind === 'appendix' && previewModel.newPreview?.kind === 'appendix') {
      const before = previewModel.oldPreview.appendix;
      const after = previewModel.newPreview.appendix;
      const tableCount = Math.max(before.tables.length, after.tables.length);
      const diffs = Array.from({ length: tableCount }, (_, index) => diffAppendixTables(before.tables[index], after.tables[index]));

      return (
        <div className="grid grid-cols-2 gap-2">
          <PreviewFrame title="Before" tone="neutral">
            <AppendixSnapshotTable appendix={before} diffs={diffs} side="before" />
          </PreviewFrame>
          <PreviewFrame title="After" tone="positive">
            <AppendixSnapshotTable appendix={after} diffs={diffs} side="after" />
          </PreviewFrame>
        </div>
      );
    }

    if (previewModel.nodeType === 'FormatValue' && formatValueRowPreviewField) {
      return (
        <div className="space-y-2">
          {previewModel.fieldLabel ? (
            <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">
              Field: {previewModel.fieldLabel}
            </div>
          ) : null}
          <FormatValueUpdatedRowTable
            field={formatValueRowPreviewField}
            oldPreview={previewModel.oldPreview}
            newPreview={previewModel.newPreview}
            code={formatValueCode}
          />
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {previewModel.fieldLabel ? (
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">
            Field: {previewModel.fieldLabel}
          </div>
        ) : null}
        <div className="grid grid-cols-2 gap-2">
          <PreviewFrame title="Before" tone="neutral">
            {renderPreviewValue(previewModel.oldPreview)}
          </PreviewFrame>
          <PreviewFrame title="After" tone="positive">
            {renderPreviewValue(previewModel.newPreview)}
          </PreviewFrame>
        </div>
      </div>
    );
  }

  if (previewModel.operation === 'delete') {
    return (
      <PreviewFrame title="Removed Value" tone="negative">
        {deletedFormatValuePreview
          ? <FormatValueSnapshotTable row={deletedFormatValuePreview.row} code={formatValueCode} />
          : renderPreviewValue(previewModel.oldPreview)}
      </PreviewFrame>
    );
  }

  return (
    <PreviewFrame title="Added Value" tone="positive">
      {renderPreviewValue(previewModel.newPreview)}
    </PreviewFrame>
  );
}
